<template>
  <PrimeDrawer
    v-model:visible="visible"
    position="right"
    :modal="false"
    :dismissable="false"
    :show-close-icon="false"
    class="tw-mt-32 tw-w-full tw-flex-1 tw-rounded-2xl tw-drop-shadow-2xl sm:tw-mt-0 sm:!tw-max-w-[400px] sm:tw-rounded-none"
    :pt="{
      content: { class: 'tw-p-6' },
      header: { class: 'tw-px-6 tw-py-4 tw-border-b tw-border-gray-200' },
      footer: { class: 'tw-p-0' },
      mask: { class: 'tw-bg-black/50' },
    }"
  >
    <template #header>
      <div class="tw-flex tw-w-full tw-items-center tw-justify-between">
        <h2 class="tw-font-outfit tw-text-2xl tw-font-medium tw-text-gray-900">Report an issue</h2>
        <button
          type="button"
          class="tw-rounded-full tw-p-2 tw-text-gray-600 hover:tw-bg-gray-100 hover:tw-text-gray-900"
          data-testid="report-issue-close"
          @click="close"
        >
          <DismissIconLarge class="tw-h-6 tw-w-6" />
        </button>
      </div>
    </template>

    <form class="tw-space-y-4" data-testid="report-issue-form" @submit="onSubmit">
      <p class="tw-text-gray-600">
        Tell us what went wrong and our team will look into it. We already know who you are, so
        there is no need to include your name or contact details.
      </p>

      <div>
        <label class="tw-text-base tw-font-medium tw-text-gray-900" for="report-issue-area">
          What is this about?
        </label>
        <PrimeSelect
          id="report-issue-area"
          v-model="area"
          :options="AREA_OPTIONS"
          placeholder="Select an option"
          filter
          :invalid="Boolean(areaErrors.length)"
          :aria-describedby="areaErrors.length ? 'report-issue-area-error' : undefined"
          data-testid="report-issue-area"
          class="tw-mt-1 tw-h-12 tw-w-full"
        />
        <div class="tw-min-h-6">
          <p
            v-if="areaErrors.length"
            id="report-issue-area-error"
            class="tw-pt-1 tw-text-sm tw-text-rose-600"
          >
            {{ areaErrors[0] }}
          </p>
        </div>
      </div>

      <div>
        <label class="tw-text-base tw-font-medium tw-text-gray-900" for="report-issue-summary">
          What happened?
        </label>
        <PrimeTextarea
          id="report-issue-summary"
          v-model="summary"
          rows="6"
          :maxlength="SUMMARY_MAX_LENGTH"
          :invalid="Boolean(summaryErrors.length)"
          :aria-describedby="summaryErrors.length ? 'report-issue-summary-error' : undefined"
          placeholder="Describe the problem and what you were trying to do."
          data-testid="report-issue-summary"
          class="tw-mt-1 tw-w-full"
        />
        <div class="tw-min-h-6">
          <p
            v-if="summaryErrors.length"
            id="report-issue-summary-error"
            class="tw-pt-1 tw-text-sm tw-text-rose-600"
          >
            {{ summaryErrors[0] }}
          </p>
        </div>
      </div>
    </form>

    <template #footer>
      <div class="tw-border-t tw-border-gray-200 tw-p-4">
        <PrimeButton
          type="submit"
          rounded
          :loading="isSubmitting"
          :class="primaryButton"
          data-testid="report-issue-submit"
          pt:root:class="tw-h-14 tw-w-full tw-border-none tw-font-semibold tw-text-white"
          @click="onSubmit"
        >
          Send report
        </PrimeButton>
      </div>
    </template>
  </PrimeDrawer>
</template>

<script setup lang="ts">
import { App } from '@capacitor/app'
import { Capacitor } from '@capacitor/core'
import { captureFeedback, captureMessage, flush, withScope } from '@sentry/vue'
import * as yup from 'yup'

import { useIsNative } from '~/composables/usePlatform'
import { useReportIssueDrawer } from '~/composables/useReportIssueDrawer'
import DismissIconLarge from '~icons/fluent/dismiss-24-regular'

const SUMMARY_MAX_LENGTH = 2000
const SENTRY_DELIVERY_TIMEOUT_MS = 5000

const AREA_OPTIONS = [
  'Uploading or viewing my files',
  'Completing a requested action',
  'Signing a document',
  'Appointments',
  'Signing in or my account',
  'Notifications',
  'Something else',
]

const { visible, close } = useReportIssueDrawer()
const { $auth } = useNuxtApp()
const $analytics = useAnalytics()
const branding = useBrand()
const route = useRoute()
const toast = useToast()

const { getThemeToken } = useTheme()
const primaryButton = getThemeToken('primary-button')

const schema = yup.object({
  area: yup.string().required('Please choose what this is about'),
  summary: yup.string().trim().required('Please tell us what happened'),
})

type FormValues = yup.InferType<typeof schema>

const { handleSubmit, isSubmitting, resetForm } = useForm<FormValues>({
  validationSchema: toTypedSchema(schema),
  initialValues: { area: '', summary: '' },
})

const { value: area, errors: areaErrors } = useField<string>('area')
const { value: summary, errors: summaryErrors } = useField<string>('summary')

async function getAppVersion() {
  if (!useIsNative()) return 'web'

  try {
    const info = await App.getInfo()
    return `${info.version} (${info.build})`
  } catch {
    return 'unknown'
  }
}

const onSubmit = handleSubmit(async values => {
  const user = $auth?.user

  const tags = {
    support_request: 'casewise',
    support_area: values.area,
    lead_id: String(user?.leadId ?? user?.lead?.id ?? ''),
    law_firm: branding.value?.lawFirm?.name ?? '',
    law_firm_id: String(branding.value?.lawFirmId ?? ''),
    platform: Capacitor.getPlatform(),
    app_version: await getAppVersion(),
    screen: route.path,
  }

  try {
    const eventId = withScope(scope => {
      scope.setLevel('info')
      scope.setTags(tags)
      return captureMessage(`Casewise support request: ${values.area}`)
    })

    captureFeedback({
      name: [user?.firstName, user?.lastName].filter(Boolean).join(' '),
      email: user?.email ?? '',
      message: values.summary,
      associatedEventId: eventId,
    })

    const delivered = await flush(SENTRY_DELIVERY_TIMEOUT_MS)
    if (!delivered) {
      throw new Error('Report delivery timed out before reaching Sentry')
    }
  } catch (error) {
    $analytics.track('ErrorReceived', {
      elementType: 'banner',
      text: 'Something went wrong',
      flow: 'report issue',
    })
    toast.add({
      severity: 'error',
      summary: 'We could not send your report',
      detail: 'Please try again, or contact us by phone or email.',
      life: 8000,
    })
    return
  }

  $analytics.trackFormSubmit({ formName: 'report issue' })

  toast.add({
    severity: 'success',
    summary: 'Report received',
    detail: 'Thanks for letting us know. Our team will review it and reach out if we need more.',
    life: 8000,
  })

  resetForm()
  close()
})
</script>
